import { ResponsiveTable } from '../components/ResponsiveTable';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  Edit, 
  Eye, 
  Calendar,
  Mail,
  MapPin,
  AlertCircle,
  Users,
  Loader2,
  LogIn,
  Trash2,
  Plus,
  Activity,
  X
} from 'lucide-react';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import { isMinor as isMinorFromBirthDate } from '@digitaltattoo/shared-utils';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { ShopColumnHeader, ShopColumnCell } from '../components/ShopListColumn';
import { ListPaginationFooter } from '../components/ListPaginationFooter';
import { useToast } from '../components/Toast';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { RootState } from '../store';
import { useRbac } from '../contexts/RbacContext';

interface Client {
  id: string;
  shopId?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string | null;
  isMinor?: boolean | null;
  isAnonymous?: boolean;
  anonymousCode?: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'VIP';
  createdAt: string;
  updatedAt: string;
  appointmentCount: number;
  lastAppointment: string | null;
  shopName?: string | null;
  hasLogin: boolean;
  address: {
    city: string;
    country: string;
  };
}

function isPlaceholderEmail(email?: string | null): boolean {
  const value = (email || '').trim().toLowerCase();
  if (!value) return false;
  if (value.endsWith('@invalid')) return true;
  if (value.startsWith('anon.')) return true;
  const localPart = value.split('@')[0] || '';
  return localPart.includes('+anon.');
}

function isAnonymousClient(client: Pick<Client, 'isAnonymous' | 'email'>): boolean {
  return Boolean(client.isAnonymous) || isPlaceholderEmail(client.email);
}

function getClientDisplayName(client: Pick<Client, 'firstName' | 'lastName' | 'isAnonymous' | 'anonymousCode' | 'email'>): string {
  const firstName = (client.firstName || '').trim();
  const lastName = (client.lastName || '').trim();
  if (isAnonymousClient(client)) {
    const label = firstName ? `${firstName} — Client anonyme` : 'Client anonyme';
    const code = (client.anonymousCode || '').trim();
    return code ? `${label} (${code})` : label;
  }
  return [firstName, lastName].filter(Boolean).join(' ') || '—';
}

function getVisibleClientEmail(client: Pick<Client, 'email'>): string {
  const email = (client.email || '').trim();
  if (!email || isPlaceholderEmail(email)) return '';
  return email;
}

function getVisibleClientPhone(client: Pick<Client, 'phone'>): string {
  return (client.phone || '').trim();
}

function getClientMinorStatus(
  client: Pick<Client, 'dateOfBirth' | 'isMinor'>
): boolean | null {
  if (client.dateOfBirth) {
    const birthDate = new Date(client.dateOfBirth);
    if (!Number.isNaN(birthDate.getTime())) {
      return isMinorFromBirthDate(client.dateOfBirth);
    }
  }

  return typeof client.isMinor === 'boolean' ? client.isMinor : null;
}

interface ShopOption {
  id: string;
  name: string;
  address?: {
    city?: string;
  };
}

interface ClientsResponse {
  success: boolean;
  data: {
    clients: Client[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

export function ClientsListPage() {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const isSuperAdmin = user?.role === 'super_admin';
  const { can } = useRbac();
  const canHardDelete = can('entities:delete:hard');
  
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement | null>(null);
  
  // Filters and pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anonymousFilter, setAnonymousFilter] = useState('');
  const [minorFilter, setMinorFilter] = useState(false);
  const [dateRange, setDateRange] = useState('');
  const [showWithoutShopOnly, setShowWithoutShopOnly] = useState(false);
  const [availableShops, setAvailableShops] = useState<ShopOption[]>([]);
  const [assignShopModalOpen, setAssignShopModalOpen] = useState(false);
  const [assigningClient, setAssigningClient] = useState<Client | null>(null);
  const [selectedAssignShopId, setSelectedAssignShopId] = useState('');
  const [assigningShop, setAssigningShop] = useState(false);
  const [assignShopError, setAssignShopError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    clientId: string | null;
    clientName: string;
  }>({
    isOpen: false,
    clientId: null,
    clientName: ''
  });
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{
    isOpen: boolean;
    clientIds: string[];
  }>({
    isOpen: false,
    clientIds: []
  });
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{
    isOpen: boolean;
    clientIds: string[];
  }>({
    isOpen: false,
    clientIds: []
  });
  const [sendingHelloClientId, setSendingHelloClientId] = useState<string | null>(null);
  const [sendHelloConfirm, setSendHelloConfirm] = useState<{ isOpen: boolean; client: Client | null }>({
    isOpen: false,
    client: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);
  
  const [limit, setLimit] = useState(20);
  const isBusy = loading || deleting || bulkHardDeleting || assigningShop;
  const { needsShopSelection, canLoad, currentShopId, isAllShopsMode } = useShopScopedPage();

  useEffect(() => {
    setPage(1);
  }, [currentShopId, isAllShopsMode]);

  useEffect(() => {
    if (!canLoad) return;
    fetchClients();
  }, [canLoad, currentShopId, isAllShopsMode, showWithoutShopOnly, search, statusFilter, anonymousFilter, minorFilter, dateRange, sortKey, sortDir, page, limit]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortKey,
        sortDir,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(anonymousFilter && { anonymous: anonymousFilter }),
        ...(minorFilter && { minor: 'true' }),
        ...(dateRange && { dateRange }),
        ...(showWithoutShopOnly && { withoutShop: 'true' }),
        ...(!showWithoutShopOnly && isAllShopsMode && { allShops: 'true' }),
      });

      const response = await apiClient(`/api/admin/clients?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }

      const data: ClientsResponse = await response.json();
      console.log('CLIENTS API:', data.data.clients);
      if (data.success) {
        setClients(data.data.clients);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
      } else {
        setError(data.message || 'Failed to load clients');
      }
    } catch (error) {
      console.error('Fetch clients error:', error);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableShops = async () => {
    try {
      const response = await apiClient('/api/admin/shops?limit=1000&allShops=true');
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Impossible de charger la liste des shops');
      }
      setAvailableShops(Array.isArray(data.data?.shops) ? data.data.shops : []);
    } catch (err: any) {
      console.error('Fetch shops for client assignment error:', err);
      setAssignShopError(err?.message || 'Impossible de charger la liste des shops');
    }
  };

  const openAssignShopModal = async (client: Client) => {
    setAssigningClient(client);
    setSelectedAssignShopId('');
    setAssignShopError(null);
    setAssignShopModalOpen(true);
    if (availableShops.length === 0) {
      await fetchAvailableShops();
    }
  };

  const closeAssignShopModal = () => {
    if (assigningShop) return;
    setAssignShopModalOpen(false);
    setAssigningClient(null);
    setSelectedAssignShopId('');
    setAssignShopError(null);
  };

  const handleAssignShop = async () => {
    if (!assigningClient || !selectedAssignShopId) return;
    try {
      setAssigningShop(true);
      setAssignShopError(null);
      const response = await apiClient(`/api/admin/clients/${assigningClient.id}/shop`, {
        method: 'PATCH',
        body: JSON.stringify({ shopId: selectedAssignShopId }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Impossible d\'associer le client au shop');
      }

      showToast('Shop attribué au client', 'success');
      setAssignShopModalOpen(false);
      setAssigningClient(null);
      setSelectedAssignShopId('');
      await fetchClients();
    } catch (err: any) {
      setAssignShopError(err?.message || 'Impossible d\'associer le client au shop');
    } finally {
      setAssigningShop(false);
    }
  };

  // Keep selection consistent with the currently displayed page
  useEffect(() => {
    setSelectedClientIds(prev => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(clients.map(c => c.id));
      const next = new Set<string>();
      for (const id of prev) {
        if (visibleIds.has(id)) next.add(id);
      }
      return next;
    });
  }, [clients]);

  const visibleClientIds = useMemo(() => clients.map(c => c.id), [clients]);
  const selectedCount = selectedClientIds.size;
  const allVisibleSelected = visibleClientIds.length > 0 && visibleClientIds.every(id => selectedClientIds.has(id));
  const someVisibleSelected = visibleClientIds.some(id => selectedClientIds.has(id));

  useEffect(() => {
    if (!selectAllCheckboxRef.current) return;
    selectAllCheckboxRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected]);

  const toggleSelectClient = (clientId: string) => {
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        // Unselect all visible
        for (const id of visibleClientIds) next.delete(id);
      } else {
        // Select all visible
        for (const id of visibleClientIds) next.add(id);
      }
      return next;
    });
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleEdit = (clientId: string) => {
    navigate(`/clients/${clientId}/edit`);
  };

  const handleSendHelloEmail = async (client: Client) => {
    const email = (client.email || '').trim();
    if (!email) return;
    setSendingHelloClientId(client.id);
    try {
      const response = await apiClient(`/api/admin/clients/${client.id}/send-hello-email`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        showToast(data.message || 'Email envoyé.', 'success');
        setSendHelloConfirm({ isOpen: false, client: null });
      } else {
        showToast(data.message || 'Échec de l\'envoi de l\'email.', 'error');
      }
    } catch {
      showToast('Erreur lors de l\'envoi de l\'email.', 'error');
    } finally {
      setSendingHelloClientId(null);
    }
  };

  const openSendHelloConfirm = (client: Client) => {
    if (client.email?.trim()) setSendHelloConfirm({ isOpen: true, client });
  };

  const cancelSendHelloConfirm = () => {
    if (!sendingHelloClientId) setSendHelloConfirm({ isOpen: false, client: null });
  };

  const confirmSendHelloEmail = () => {
    if (sendHelloConfirm.client) handleSendHelloEmail(sendHelloConfirm.client);
  };

  const handleView = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  const handleDelete = (clientId: string, clientName: string) => {
    setDeleteConfirm({
      isOpen: true,
      clientId,
      clientName
    });
  };

  const getFriendlyDeleteErrorMessage = (errorMessage: string) => {
    let userFriendlyMessage = errorMessage;

    if (
      errorMessage.toLowerCase().includes('foreign key') ||
      errorMessage.toLowerCase().includes('constraint') ||
      errorMessage.toLowerCase().includes('violates foreign key') ||
      errorMessage.toLowerCase().includes('échec de la suppression') ||
      errorMessage.toLowerCase().includes('failed to delete')
    ) {
      userFriendlyMessage =
        'Impossible de supprimer ce client car il est lié à d\'autres données (rendez-vous, factures, etc.). Veuillez supprimer ou modifier ces données avant de supprimer le client.';
    }

    if (
      errorMessage.includes('invoices or signed contracts') ||
      errorMessage.includes('factures') && errorMessage.includes('contrats')
    ) {
      userFriendlyMessage =
        'Suppression définitive impossible : ce client a des factures ou contrats signés (obligation légale de conservation). Utilisez la suppression standard (effacement RGPD).';
    }

    if (errorMessage.toLowerCase().includes('failed to delete client')) {
      userFriendlyMessage =
        'Échec de la suppression du client. Le client est peut-être lié à d\'autres données.';
    }

    return userFriendlyMessage;
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.clientId) return;
    
    try {
      setDeleting(true);
      setError(null); // Clear any previous errors
      
      const response = await apiClient(`/api/admin/clients/${deleteConfirm.clientId}`, {
        method: 'DELETE',
      });

      // Try to parse response as JSON
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If response is not JSON, treat as error
        setDeleteConfirm({ isOpen: false, clientId: null, clientName: '' });
        showToast('Une erreur est survenue lors de la suppression du client.', 'error', 7000);
        return;
      }
      
      if (!response.ok || !data.success) {
        // Check for foreign key constraint errors
        const errorMessage = data.message || data.error || 'Échec de la suppression du client';
        const userFriendlyMessage = getFriendlyDeleteErrorMessage(errorMessage);
        
        // Close the modal and show error toast
        setDeleteConfirm({ isOpen: false, clientId: null, clientName: '' });
        showToast(userFriendlyMessage, 'error', 7000);
        return;
      }
      
      // Success: Close the modal and refresh the list
      setDeleteConfirm({ isOpen: false, clientId: null, clientName: '' });
      await fetchClients();
      showToast('Client supprimé avec succès', 'success');
    } catch (error) {
      console.error('Delete client error:', error);
      // Close the modal and show error toast
      setDeleteConfirm({ isOpen: false, clientId: null, clientName: '' });
      showToast('Une erreur est survenue lors de la suppression du client. Le client est peut-être lié à d\'autres données.', 'error', 7000);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ isOpen: false, clientId: null, clientName: '' });
  };

  const openBulkDeleteConfirm = () => {
    if (selectedClientIds.size === 0) return;
    setBulkDeleteConfirm({
      isOpen: true,
      clientIds: Array.from(selectedClientIds)
    });
  };

  const cancelBulkDelete = () => {
    setBulkDeleteConfirm({ isOpen: false, clientIds: [] });
  };

  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.clientIds.length === 0) return;
    try {
      setDeleting(true);
      setError(null);

      const failed: Array<{ id: string; message: string }> = [];

      for (const clientId of bulkDeleteConfirm.clientIds) {
        try {
          const response = await apiClient(`/api/admin/clients/${clientId}`, { method: 'DELETE' });
          let data: any = null;
          try {
            data = await response.json();
          } catch (parseError) {
            failed.push({ id: clientId, message: 'Réponse du serveur invalide' });
            continue;
          }
          if (!response.ok || !data?.success) {
            const errorMessage = data?.message || data?.error || 'Échec de la suppression du client';
            failed.push({ id: clientId, message: getFriendlyDeleteErrorMessage(errorMessage) });
          }
        } catch (e) {
          failed.push({
            id: clientId,
            message: 'Une erreur est survenue lors de la suppression du client.'
          });
        }
      }

      setBulkDeleteConfirm({ isOpen: false, clientIds: [] });
      setSelectedClientIds(new Set());
      await fetchClients();

      const deletedCount = bulkDeleteConfirm.clientIds.length - failed.length;
      if (failed.length === 0) {
        showToast(`${deletedCount} client${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''} avec succès`, 'success');
      } else if (deletedCount === 0) {
        showToast(`Aucun client supprimé. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`, 'error', 7000);
      } else {
        showToast(`${deletedCount} client${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''}. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`, 'error', 7000);
      }
    } finally {
      setDeleting(false);
    }
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedClientIds.size === 0) return;
    setBulkHardDeleteConfirm({
      isOpen: true,
      clientIds: Array.from(selectedClientIds)
    });
  };

  const cancelBulkHardDelete = () => {
    setBulkHardDeleteConfirm({ isOpen: false, clientIds: [] });
  };

  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.clientIds.length === 0) return;
    try {
      setBulkHardDeleting(true);
      setError(null);

      const failed: Array<{ id: string; message: string }> = [];

      for (const clientId of bulkHardDeleteConfirm.clientIds) {
        try {
          const response = await apiClient(`/api/admin/clients/${clientId}?hardDelete=true`, { method: 'DELETE' });
          let data: any = null;
          try {
            data = await response.json();
          } catch {
            failed.push({ id: clientId, message: 'Réponse du serveur invalide' });
            continue;
          }
          if (!response.ok || !data?.success) {
            const errorMessage = data?.message || data?.error || 'Échec de la suppression définitive du client';
            failed.push({ id: clientId, message: errorMessage });
          }
        } catch {
          failed.push({
            id: clientId,
            message: 'Une erreur est survenue lors de la suppression définitive du client.'
          });
        }
      }

      setBulkHardDeleteConfirm({ isOpen: false, clientIds: [] });
      setSelectedClientIds(new Set());
      await fetchClients();

      const deletedCount = bulkHardDeleteConfirm.clientIds.length - failed.length;
      if (failed.length === 0) {
        showToast(`${deletedCount} client${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''} définitivement`, 'success');
      } else if (deletedCount === 0) {
        showToast(`Aucun client supprimé définitivement. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`, 'error', 7000);
      } else {
        showToast(`${deletedCount} client${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''} définitivement. ${failed.length} échec${failed.length !== 1 ? 's' : ''}.`, 'error', 7000);
      }
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-800', label: 'Actif' },
      INACTIVE: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inactif' },
      SUSPENDED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Suspendu' },
      VIP: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'VIP' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.ACTIVE;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des clients...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${isBusy ? 'cursor-wait' : ''}`}>
      {/* Toast Notifications */}
      <ToastComponent />
      
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Gestion des Clients</span>
              <HelpIcon 
                docPath="05-gestion-clients#05-2-liste" 
                tooltip="Documentation : Liste des clients"
              />
            </h1>
            <p className="text-textSecondary mt-1">
              {total} client{total !== 1 ? 's' : ''} au total
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {isSuperAdmin && (showWithoutShopOnly ? (
              <button
                onClick={() => {
                  setShowWithoutShopOnly(false);
                  setPage(1);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Retour à la liste</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setShowWithoutShopOnly(true);
                  setPage(1);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <AlertCircle className="w-4 h-4" />
                <span>Clients sans shop</span>
              </button>
            ))}
            {selectedCount > 0 ? (
              <>
                <button
                  onClick={openBulkDeleteConfirm}
                  disabled={deleting || bulkHardDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Supprimer les clients sélectionnés"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Supprimer ({selectedCount})</span>
                </button>
                {canHardDelete && (
                  <button
                    onClick={openBulkHardDeleteConfirm}
                    disabled={deleting || bulkHardDeleting}
                    className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                    title="Supprimer définitivement les clients sélectionnés"
                  >
                    <AlertCircle className="w-4 h-4" />
                    <span>Suppression définitive ({selectedCount})</span>
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/quick-client-create')}
                  className="btn-secondary inline-flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Création rapide</span>
                </button>
                <button
                  onClick={() => navigate('/clients/create-full')}
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Nouveau client</span>
                </button>
              </>
            )}
          </div>
        </div>
        {showWithoutShopOnly && (
          <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center">
            Vue des clients sans shop : utilisez « Attribuer un shop » pour les rattacher.
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Nom, email, téléphone, code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les statuts</option>
              <option value="ACTIVE">Actif</option>
              <option value="INACTIVE">Inactif</option>
              <option value="SUSPENDED">Suspendu</option>
              <option value="VIP">VIP</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Anonymous Filter */}
          <div className="relative">
            <select
              value={anonymousFilter}
              onChange={(e) => {
                setAnonymousFilter(e.target.value);
                setPage(1);
              }}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les clients</option>
              <option value="true">Clients anonymes</option>
              <option value="false">Clients nominatifs</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Minor Filter */}
          <label className="flex min-h-[42px] items-center gap-2">
            <input
              type="checkbox"
              checked={minorFilter}
              onChange={(e) => {
                setMinorFilter(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Mineurs uniquement</span>
          </label>

          {/* Date Range Filter */}
          <div className="relative">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="quarter">Ce trimestre</option>
              <option value="year">Cette année</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearch('');
              setStatusFilter('');
              setAnonymousFilter('');
              setMinorFilter(false);
              setDateRange('');
              setPage(1);
            }}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchClients}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Clients Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={clients.length === 0 || deleting || bulkHardDeleting}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label="Sélectionner tous les clients de la page"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('firstName')}
                >
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>Client</span>
                    {sortKey === 'firstName' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center space-x-1">
                    <Mail className="w-4 h-4" />
                    <span>Contact</span>
                    {sortKey === 'email' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Statut</span>
                    {sortKey === 'status' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('isMinor')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Mineur</span>
                    {sortKey === 'isMinor' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <ShopColumnHeader
                  visible={isAllShopsMode && !showWithoutShopOnly}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('hasLogin')}
                >
                  <div className="flex items-center space-x-1">
                    <LogIn className="w-4 h-4" />
                    <span>A un compte</span>
                    {sortKey === 'hasLogin' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('appointmentCount')}
                >
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>RDV</span>
                    {sortKey === 'appointmentCount' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Créé le</span>
                    {sortKey === 'createdAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clients.map((client) => (
                <tr key={client.id} className="group hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedClientIds.has(client.id)}
                      disabled={deleting || bulkHardDeleting}
                      onChange={() => toggleSelectClient(client.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      aria-label={`Sélectionner ${getClientDisplayName(client)}`}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      {isAnonymousClient(client) ? (
                        <>
                          {client.firstName?.trim() ? (
                            <span className="text-sm font-medium text-gray-900">
                              {client.firstName.trim()}
                            </span>
                          ) : null}
                          <div className={client.firstName?.trim() ? 'text-sm text-gray-500' : 'text-sm font-medium text-gray-900'}>
                            Client anonyme
                          </div>
                          {client.anonymousCode?.trim() ? (
                            <div className="text-xs font-mono text-gray-500">
                              {client.anonymousCode.trim()}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">
                          {getClientDisplayName(client)}
                        </span>
                      )}
                      {client.address?.city && (
                        <div className="text-sm text-gray-500 flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {client.address.city}, {client.address.country}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getVisibleClientEmail(client) || '—'}</div>
                    {getVisibleClientPhone(client) ? (
                      <div className="text-sm text-gray-500">{getVisibleClientPhone(client)}</div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(client.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getClientMinorStatus(client) === true ? (
                      <span className="text-sm font-semibold text-amber-700">Oui</span>
                    ) : getClientMinorStatus(client) === false ? (
                      <span className="text-sm text-gray-700">Non</span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <ShopColumnCell visible={isAllShopsMode && !showWithoutShopOnly} shopName={client.shopName} />
                  <td className="px-6 py-4 whitespace-nowrap">
                    {client.hasLogin ? (
                      <span className="text-sm font-medium text-green-600">Oui</span>
                    ) : (
                      <span className="text-sm text-gray-500">Non</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{client.appointmentCount || 0}</div>
                    {client.lastAppointment && (
                      <div className="text-xs text-gray-500">
                        Dernier: {formatDate(client.lastAppointment)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(client.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end space-x-2">
                      {showWithoutShopOnly ? (
                        <button
                          onClick={() => openAssignShopModal(client)}
                          className="px-2 py-1 text-xs font-medium rounded border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
                          title="Attribuer un shop"
                        >
                          Attribuer un shop
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => openSendHelloConfirm(client)}
                            disabled={!getVisibleClientEmail(client) || sendingHelloClientId === client.id}
                            className="text-indigo-600 hover:text-indigo-700 p-1 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                            title={getVisibleClientEmail(client) ? 'Envoyer un email de bienvenue' : 'Aucune adresse email'}
                          >
                            {sendingHelloClientId === client.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => navigate(`/clients/${client.id}/activity`)}
                            className="text-purple-600 hover:text-purple-700 p-1 rounded"
                            title="Activités du client"
                          >
                            <Activity className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleView(client.id)}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded"
                            title="Voir le client"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(client.id)}
                            className="text-primary-600 hover:text-primary-700 p-1 rounded"
                            title="Modifier le client"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(client.id, getClientDisplayName(client))}
                        className="text-red-600 hover:text-red-700 p-1 rounded"
                        title="Supprimer le client"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Empty State */}
        {!loading && clients.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun client trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || statusFilter || anonymousFilter || dateRange 
                ? 'Essayez de modifier vos filtres de recherche.'
                : 'Commencez par créer votre premier client.'
              }
            </p>
          </div>
        )}

        <ListPaginationFooter
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          disabled={isBusy}
          onPageChange={setPage}
          onLimitChange={(next) => {
            setLimit(next);
            setPage(1);
          }}
        />
      </div>

      {/* Loading overlay for subsequent loads */}
      {loading && clients.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 cursor-wait">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement...</span>
          </div>
        </div>
      )}

      {assignShopModalOpen && assigningClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Attribuer un shop</h3>
            <p className="text-sm text-gray-600 mb-4">
              {getClientDisplayName(assigningClient)}
            </p>

            {assignShopError && (
              <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {assignShopError}
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-2">Shop</label>
            <select
              value={selectedAssignShopId}
              onChange={(event) => setSelectedAssignShopId(event.target.value)}
              className="input-field mb-5"
              disabled={assigningShop}
            >
              <option value="">Sélectionner un shop</option>
              {availableShops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}{shop.address?.city ? ` - ${shop.address.city}` : ''}
                </option>
              ))}
            </select>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeAssignShopModal}
                className="btn-secondary"
                disabled={assigningShop}
              >
                Annuler
              </button>
              <button
                onClick={handleAssignShop}
                disabled={!selectedAssignShopId || assigningShop}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigningShop ? 'Attribution...' : 'Attribuer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Hello Email Confirmation Dialog */}
      {sendHelloConfirm.isOpen && sendHelloConfirm.client && (
        <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${sendingHelloClientId ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Envoyer un email de bienvenue</h3>
                  <p className="text-indigo-100 text-sm mt-0.5">Un message amical sera envoyé au client</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 mb-4">
                Envoyer l&apos;email à :
              </p>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
                <p className="font-medium text-gray-900">
                  {getClientDisplayName(sendHelloConfirm.client)}
                </p>
                <p className="text-sm text-gray-600 mt-1">{getVisibleClientEmail(sendHelloConfirm.client)}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
              <button
                type="button"
                onClick={cancelSendHelloConfirm}
                disabled={!!sendingHelloClientId}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmSendHelloEmail}
                disabled={!!sendingHelloClientId}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {sendingHelloClientId ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Envoi...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    <span>Envoyer l&apos;email</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer le client <strong>{deleteConfirm.clientName}</strong> ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Suppression...</span>
                  </>
                ) : (
                  <span>Supprimer</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${deleting ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.clientIds.length}</strong> client{bulkDeleteConfirm.clientIds.length !== 1 ? 's' : ''} ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={deleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Suppression...</span>
                  </>
                ) : (
                  <span>Supprimer</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Hard Delete: two-step confirmation */}
      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel={`client${bulkHardDeleteConfirm.clientIds.length !== 1 ? 's' : ''}`}
        count={bulkHardDeleteConfirm.clientIds.length}
        isLoading={bulkHardDeleting}
      />
    </div>
  );
}
